import { Controller } from 'stimulus'
import {
  clearCsvError,
  csvForm,
  persistCsvSettings,
  revalidateCsv,
  setPresenceValidator
} from '../../src/client_side_validations'

// Keeps community_class[starts] in sync with the inline Flatpickr date + time pickers.
export default class extends Controller {
  static targets = ['starts', 'date', 'time', 'repeatFields', 'repeatToggle', 'repeatTill']

  connect() {
    // Child flatpickr controllers connect after this one.
    requestAnimationFrame(() => {
      this.syncStarts()
      this.syncRepeatTillMinDate()
      this.toggleRepeatFields()
      this.syncScheduleCsvValidators()
    })
  }

  dateChanged() {
    this.syncStarts()
    this.syncRepeatTillMinDate()
    revalidateCsv(this.dateTarget)
  }

  timeChanged() {
    this.syncStarts()
    revalidateCsv(this.timeTarget)
  }

  toggleRepeatFields() {
    if (!this.hasRepeatFieldsTarget || !this.hasRepeatToggleTarget) return

    const on = this.repeatToggleTarget.checked
    this.repeatFieldsTarget.classList.toggle('d-none', !on)
    this.setRepeatFieldsDisabled(!on)
    this.syncRepeatCsvValidators(on)

    if (!on) this.clearRepeatCsvErrors()
    if (on) this.syncRepeatTillMinDate()
  }

  syncStarts() {
    if (!this.hasDateTarget || !this.hasTimeTarget || !this.hasStartsTarget) return

    const date = this.dateTarget.value
    const time = this.timeValue()
    if (!date || !time) {
      this.startsTarget.value = ''
      return
    }

    this.startsTarget.value = `${date} ${time}`
  }

  // Repeat Until must be after the start day and within 6 months of the start date.
  syncRepeatTillMinDate() {
    const fp = this.repeatTillFlatpickr()
    if (!fp) return

    const minDate = this.repeatTillMinDate()
    const maxDate = this.repeatTillMaxDate()
    fp.set('minDate', minDate)
    fp.set('maxDate', maxDate)

    const selected = fp.selectedDates[0]
    if (selected && (selected < minDate || selected > maxDate)) this.clearRepeatTill()
  }

  startDateValue() {
    const date = this.hasDateTarget && this.dateTarget.value
    if (!date) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    }

    return new Date(`${date}T00:00:00`)
  }

  repeatTillMinDate() {
    const minDate = this.startDateValue()
    minDate.setDate(minDate.getDate() + 1)
    return minDate
  }

  // Matches CommunityClass#repeat_till_date_limit (starts + 6.months).
  repeatTillMaxDate() {
    const maxDate = this.startDateValue()
    maxDate.setMonth(maxDate.getMonth() + 6)
    return maxDate
  }

  setRepeatFieldsDisabled(disabled) {
    this.setRepeatFrequencyDisabled(disabled)
    this.setRepeatTillDisabled(disabled)
  }

  setRepeatFrequencyDisabled(disabled) {
    const frequency = this.repeatFrequencyInput()
    if (!frequency) return

    const $frequency = $(frequency)
    $frequency.prop('disabled', disabled)
    if ($frequency.data('select2')) $frequency.trigger('change.select2')
  }

  setRepeatTillDisabled(disabled) {
    if (!this.hasRepeatTillTarget) return

    this.repeatTillTarget.disabled = disabled
    const fp = this.repeatTillFlatpickr()
    if (fp && fp.altInput) fp.altInput.disabled = disabled
  }

  clearRepeatTill() {
    if (!this.hasRepeatTillTarget) return

    this.repeatTillTarget.value = ''
    if (this.repeatTillTarget._flatpickr) this.repeatTillTarget._flatpickr.clear()
  }

  // Date/time are UI-only inputs that compose hidden starts. Inject presence so
  // CSV can validate them; drop starts so the hidden field does not double-error.
  // Persist back to the data attribute: ClientSideValidations.enable() re-parses
  // that attribute on every submit and would otherwise wipe runtime injections.
  syncScheduleCsvValidators() {
    const form = csvForm(this.element)
    if (!form) return

    ClientSideValidations.enable(form)
    const validators = form.ClientSideValidations.settings.validators

    if (this.hasStartsTarget) delete validators[this.startsTarget.name]

    if (this.hasDateTarget) setPresenceValidator(validators, this.dateTarget.name)
    if (this.hasTimeTarget) setPresenceValidator(validators, this.timeTarget.name)

    persistCsvSettings(form)
  }

  // Recurring is a UI-only toggle — model presence validators are conditional and
  // are not embedded for a blank new form. Inject them while the toggle is on.
  syncRepeatCsvValidators(on) {
    const form = csvForm(this.element)
    if (!form) return

    ClientSideValidations.enable(form)
    const validators = form.ClientSideValidations.settings.validators
    const frequencyName = this.repeatFrequencyInput()?.name
    const tillName = this.hasRepeatTillTarget && this.repeatTillTarget.name
    if (!frequencyName || !tillName) return

    if (on) {
      setPresenceValidator(validators, frequencyName)
      setPresenceValidator(validators, tillName)
    } else {
      if (validators[frequencyName]) delete validators[frequencyName].presence
      delete validators[tillName]
    }

    persistCsvSettings(form)
  }

  clearRepeatCsvErrors() {
    clearCsvError(this.repeatFrequencyInput())
    if (this.hasRepeatTillTarget) clearCsvError(this.repeatTillTarget)
  }

  repeatFrequencyInput() {
    return this.repeatFieldsTarget.querySelector('[name*="[repeat_frequency]"]')
  }

  repeatTillFlatpickr() {
    return this.hasRepeatTillTarget && this.repeatTillTarget._flatpickr
  }

  timeValue() {
    const raw = this.timeTarget.value
    if (!raw) return null

    // Native <input type="time"> yields HH:MM (sometimes HH:MM:SS).
    return raw.slice(0, 5)
  }
}
